import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { Button, Spin, Tooltip, message } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import {
  PrintReadinessProvider,
  useMarkPrintEvent,
  usePrintReadinessPending
} from "./print-readiness";
import RenderProgressBar from "./render-progress-bar";
import { useReadyNotification } from "./use-ready-notification";
import { getScheduleImpactForMaterial } from "services/schedule-versions-services";
import { ProjectContext } from "context/ProjectProvider";
import { AppLogoIcon, CiqIconBox } from "components/svg-icons";
import { ErrorMessages } from "../../../../../constants";
import { DATE_FORMAT_MMDDYYYY_HHMM_A_Z, DateUtils } from "utils/dateutils";
import {
  formatLinkedSubmittalDataForGoverningMaterial,
  getFormattedMaterialImpactData
} from "../utils";
import {
  flattenSubmittalsForGrid,
  flattenMaterialsForGrid,
  stripHeavyFieldsForPrint,
  TSubmittalGridRow,
  TMaterialGridRow
} from "../grid-utils";
import { TMaterialImpactAnalysis } from "../models";
import TableView from "./table-view";
import { readPrintPayloadFromHash, PrintPayload } from "./print-payload";

const defaultImpactAnalysisData: TMaterialImpactAnalysis = {
  HIGH: {},
  MEDIUM: {},
  LOW: {},
  DELETED: {}
} as TMaterialImpactAnalysis;

// Tighter than plain row chunks because each row mounts an SVG timeline.
// 20 keeps each idle batch under ~16 ms on typical laptops.
const CHUNK_SIZE = 20;

// Above this row count diagrams render as rasterized PNGs instead of live
// SVGs — under it the live SVG is cheaper and stays interactive.
const RASTERIZE_THRESHOLD = 50;

function SummaryStat({
  label,
  value,
  dot
}: {
  label: string;
  value: number;
  dot?: string;
}) {
  return (
    <div
      className={
        "flex flex-col items-center justify-center gap-1.5 px-4 py-3 " +
        "border-l border-gray-300 print:border-gray-400 first:border-l-0 " +
        "min-w-[150px]"
      }
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold text-center leading-tight">
        {dot ? (
          <span
            aria-hidden
            className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: dot }}
          />
        ) : null}
        {label}
      </span>
      <span className="text-[18px] font-bold tabular-nums text-gray-900 leading-none">
        {value}
      </span>
    </div>
  );
}

const ORIENTATION = "landscape" as const;

function OptimizedPrintView() {
  return (
    <PrintReadinessProvider>
      <OptimizedPrintViewInner />
    </PrintReadinessProvider>
  );
}

function OptimizedPrintViewInner() {
  const [sia, setSia] = useState(defaultImpactAnalysisData);
  const [mia, setMia] = useState(defaultImpactAnalysisData);
  const [isLoading, setIsLoading] = useState(true);
  const [renderComplete, setRenderComplete] = useState(false);
  const [mountProgress, setMountProgress] = useState<{
    visible: number;
    total: number;
  }>({ visible: 0, total: 0 });
  const markEvent = useMarkPrintEvent();

  const {
    isFeatureFlagEnabled,
    projectToken,
    projectDetails,
    isProductEntitlementsDataAvailable
  } = useContext(ProjectContext);
  const isAffectedMaterialEnabled = isFeatureFlagEnabled("MATERIAL_IMPACT");

  const location = useLocation();
  const params = useParams<{ projectId?: string }>();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const importLogId = searchParams.get("importLogId") || "";
  const sourceLogId = searchParams.get("sourceLogId") || "";
  const tab = (searchParams.get("tab") || "submittals") as
    | "submittals"
    | "materials";
  const projectId = params.projectId || "";

  const locationHash = location.hash;
  const printPayload: PrintPayload | null = useMemo(
    () => readPrintPayloadFromHash(locationHash),
    [locationHash]
  );

  useEffect(() => {
    if (!importLogId || !sourceLogId || !projectToken) return undefined;

    const controller = new AbortController();
    (async () => {
      markEvent("fetch:start");
      try {
        const res: any = await getScheduleImpactForMaterial(
          importLogId,
          sourceLogId,
          projectToken,
          controller.signal
        );
        markEvent("fetch:end");
        if (controller.signal.aborted) return;
        if (res?.data) {
          const responseBody = res.data;
          const submittalImpactAnalysis =
            responseBody.success?.impactAnalysis?.submittals ||
            defaultImpactAnalysisData;
          // `|| defaultImpactAnalysisData` (a non-null const) makes this always truthy
          /* v8 ignore next */
          if (submittalImpactAnalysis) {
            const allMaterialDateBlock =
              responseBody.success.all_material_date_block;
            setSia(
              formatLinkedSubmittalDataForGoverningMaterial(
                submittalImpactAnalysis,
                allMaterialDateBlock
              )
            );
          }
          const materialImpactAnalysis =
            responseBody.success?.impactAnalysis?.materials ||
            defaultImpactAnalysisData;
          // `|| defaultImpactAnalysisData` (a non-null const) makes this always truthy
          /* v8 ignore next */
          if (materialImpactAnalysis) {
            const allSubmittalDateBlock =
              responseBody.success.all_submittal_date_block;
            setMia(
              getFormattedMaterialImpactData(
                materialImpactAnalysis,
                allSubmittalDateBlock
              )
            );
          }
        } else {
          message.error(res?.message || ErrorMessages.SomethingWentWrong);
        }
      } catch {
        if (!controller.signal.aborted) {
          message.error(ErrorMessages.SomethingWentWrong);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [importLogId, sourceLogId, projectToken, markEvent]);

  const submittalRows = useMemo(
    () => stripHeavyFieldsForPrint(flattenSubmittalsForGrid(sia, true)),
    [sia]
  );
  const materialRows = useMemo(
    () => stripHeavyFieldsForPrint(flattenMaterialsForGrid(mia, true)),
    [mia]
  );

  const rows = useMemo(() => {
    const source = tab === "materials" ? materialRows : submittalRows;
    if (!printPayload?.filteredIds) return source;
    const byId = new Map(source.map((r) => [r.id, r]));
    const ordered: (TSubmittalGridRow | TMaterialGridRow)[] = [];
    for (const id of printPayload.filteredIds) {
      const row = byId.get(id);
      if (row) ordered.push(row);
    }
    return ordered;
  }, [tab, submittalRows, materialRows, printPayload]);

  const riskCounts = useMemo(() => {
    const counts = { HIGH: 0, MEDIUM: 0, LOW: 0, DELETED: 0 };
    for (const r of rows) {
      const level = r.risk_level as keyof typeof counts;
      if (level in counts) counts[level] += 1;
    }
    return counts;
  }, [rows]);

  const onRenderComplete = useCallback(() => {
    setRenderComplete(true);
  }, []);

  const autoprintRequested = searchParams.get("autoprint") === "1";

  const mountStartedRef = useRef(false);
  const mountEndedRef = useRef(false);

  const handleMountProgress = useCallback(
    (visible: number, total: number) => {
      setMountProgress({ visible, total });
      if (!mountStartedRef.current && visible > 0) {
        mountStartedRef.current = true;
        markEvent("mount:start");
      }
      if (!mountEndedRef.current && total > 0 && visible >= total) {
        mountEndedRef.current = true;
        markEvent("mount:end");
      }
    },
    [markEvent]
  );

  const generatedAt = useMemo(() => new Date(), []);

  const isMaterialsTab = tab === "materials";
  const coverTitle = isMaterialsTab
    ? "Schedule Change Impact on Materials"
    : "Schedule Change Impact on Submittals";
  const entityNoun = isMaterialsTab ? "Materials" : "Submittals";

  const pendingForNotify = usePrintReadinessPending();
  const readyForNotify =
    rows.length === 0 || (renderComplete && pendingForNotify === 0);
  useReadyNotification({
    ready: readyForNotify,
    rowCount: rows.length,
    projectName: projectDetails?.name || "Project",
    title: tab === "materials" ? "Impacted Materials" : "Impacted Submittals"
  });

  const pageSizeCSS = "Letter";
  const orientation = ORIENTATION;
  const projectName = (projectDetails?.name || "Project").replace(/["\\]/g, "");
  // Locked to MM-DD-YYYY (not toLocaleString) so a PDF generated by a UK
  // user doesn't mix DD/MM/YYYY here with MM-DD-YYYY in the data cells.
  const generatedStr = (
    DateUtils.format(generatedAt, DATE_FORMAT_MMDDYYYY_HHMM_A_Z) ||
    generatedAt.toISOString()
  ).replace(/["\\]/g, "");
  // print-color-adjust: exact forces backgrounds + colored text into the
  // print preview — browsers strip them by default to save ink.
  const pageStyle = `
    @page {
      size: ${pageSizeCSS} ${orientation};
      margin: 15mm 10mm 15mm 10mm;
      @top-left {
        content: "${projectName}";
        font-size: 9pt;
        color: #6b7280;
      }
      @top-right {
        content: "${generatedStr}";
        font-size: 9pt;
        color: #6b7280;
      }
      @bottom-right {
        content: "Page " counter(page) " of " counter(pages);
        font-size: 9pt;
        color: #6b7280;
      }
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;

  const rootClass =
    "bg-white text-gray-900 px-8 py-6 print:px-2 print:py-0 text-[12px] leading-snug";

  // Wait for entitlements to load before showing the "not enabled" copy —
  // flags return false during hydration which would otherwise flash this
  // message before the spinner takes over.
  if (
    tab === "materials" &&
    isProductEntitlementsDataAvailable &&
    !isAffectedMaterialEnabled
  ) {
    return (
      <div className={rootClass}>
        <div className="p-12">Material Impact is not enabled.</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={rootClass}>
        <div
          className="min-h-[70vh] w-full flex flex-col items-center justify-center gap-4 print:hidden"
          data-testid="print-view-loading-indicator"
        >
          <Spin size="large" />
          <div className="text-sm text-gray-600">
            Please wait while the print view loads — the print dialog will open
            automatically when it&apos;s ready.
          </div>
        </div>
      </div>
    );
  }

  const kind = tab === "materials" ? "material" : "submittal";
  const iconFeature = kind === "material" ? "MATERIALS" : "SUBMITTALS";
  const tabTitle =
    tab === "materials" ? "Impacted Materials" : "Impacted Submittals";

  return (
    <div className={rootClass}>
      <style>{pageStyle}</style>

      {/* Screen-only top toolbar — title, count, and Print on the same row.
          Sticks to the top of the viewport while scrolling. Hidden in print
          since the ceremonial header below takes over for the PDF. */}
      <div className="sticky top-0 z-20 -mx-8 mb-4 bg-white/95 backdrop-blur border-b border-gray-200 print:hidden">
        <div className="w-full px-8 py-3 flex gap-3 items-center justify-between flex-wrap">
          <span className="flex items-center gap-2 min-w-0">
            <CiqIconBox feature={iconFeature} size={15} padding={7} />
            <span className="text-base font-medium text-gray-900">
              {tabTitle} ({rows.length})
            </span>
          </span>
          <div className="flex gap-3 items-center flex-wrap">
            <span className="text-xs text-gray-500 hidden lg:inline">
              Letter · Landscape
            </span>
            <PrintLaunchButton
              renderComplete={renderComplete}
              rowCount={rows.length}
              autoprintRequested={autoprintRequested}
            />
          </div>
        </div>
      </div>

      {/* Print-only cover-page block — logo, ceremonial title, and project
          metadata. Hidden on screen because the sticky toolbar above is
          enough; the PDF needs the full cover for context. No page break
          so the summary stats below stay on the same first page. */}
      <div className="hidden print:flex print:flex-col print:items-center print:text-center print:gap-5 print:mb-10 print:pb-8 print:border-b-2 print:border-gray-400 print:pt-[10%] print:max-w-5xl print:mx-auto">
        <div className="print:w-[160px] shrink-0">
          <AppLogoIcon />
        </div>
        <div className="print:text-[22pt] font-bold leading-tight">
          {coverTitle}
        </div>
        <div className="print:text-[10pt] text-gray-500 leading-relaxed space-y-0.5">
          <div className="font-semibold text-gray-700 print:text-[12pt]">
            {projectDetails?.name || "Project"}
          </div>
          {printPayload?.sourceScheduleName ? (
            <div>Active schedule: {printPayload.sourceScheduleName}</div>
          ) : null}
          {printPayload?.importScheduleName ? (
            <div>Compared with: {printPayload.importScheduleName}</div>
          ) : null}
          <div>Generated: {generatedStr}</div>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="mb-4 print:break-inside-avoid print:flex print:flex-col print:items-center">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5 print:text-[8pt] print:text-center">
            Summary
          </div>
          <div
            className={
              "inline-flex items-stretch rounded-md overflow-hidden " +
              "border border-gray-300 print:border-gray-400 " +
              "bg-gray-50 print:bg-gray-50"
            }
          >
            <SummaryStat
              label={`Total ${entityNoun} Impacted`}
              value={rows.length}
            />
            <SummaryStat
              label={`${entityNoun} in High Risk`}
              value={riskCounts.HIGH}
              dot="#dc2626"
            />
            <SummaryStat
              label={`${entityNoun} in Medium Risk`}
              value={riskCounts.MEDIUM}
              dot="#d97706"
            />
            <SummaryStat
              label={`${entityNoun} in Low Risk`}
              value={riskCounts.LOW}
              dot="#16a34a"
            />
            <SummaryStat
              label="Activities Deleted"
              value={riskCounts.DELETED}
              dot="#9ca3af"
            />
          </div>
        </div>
      )}

      <div className="mt-4 w-full">
        {rows.length === 0 ? (
          <div className="p-4 text-gray-500">
            No rows match the filters applied.
          </div>
        ) : (
          <TableView
            key={`table-${kind}`}
            kind={kind}
            rows={rows as (TSubmittalGridRow | TMaterialGridRow)[]}
            projectId={projectId}
            chunkSize={CHUNK_SIZE}
            onComplete={onRenderComplete}
            onProgress={handleMountProgress}
            rasterize={rows.length > RASTERIZE_THRESHOLD}
            visibleColumnIds={printPayload?.visibleColumnIds}
          />
        )}
      </div>
      <RenderProgressBar
        mountVisible={mountProgress.visible}
        mountTotal={mountProgress.total}
        renderComplete={renderComplete}
      />
    </div>
  );
}

function PrintLaunchButton({
  renderComplete,
  rowCount,
  autoprintRequested
}: {
  renderComplete: boolean;
  rowCount: number;
  autoprintRequested: boolean;
}) {
  const pendingRaster = usePrintReadinessPending();
  const markEvent = useMarkPrintEvent();
  const isReady = rowCount === 0 || (renderComplete && pendingRaster === 0);

  const readyMarkedRef = useRef(false);
  useEffect(() => {
    if (isReady && !readyMarkedRef.current && rowCount > 0) {
      readyMarkedRef.current = true;
      markEvent("ready:at");
    }
  }, [isReady, rowCount, markEvent]);

  const canAutoprint = rowCount > 0 && isReady;

  const handleClickPrint = useCallback(() => {
    markEvent("print:fire");
    window.print();
  }, [markEvent]);

  // 500 ms debounce so the autoprint snapshot doesn't fire in the same
  // render tick the rasterized <img>s receive their blob src — Chrome's
  // print snapshot would catch them as placeholders.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!autoprintRequested || autoFiredRef.current) return undefined;
    if (!canAutoprint) return undefined;
    const t = window.setTimeout(() => {
      autoFiredRef.current = true;
      markEvent("print:fire");
      window.print();
    }, 500);
    return () => window.clearTimeout(t);
  }, [autoprintRequested, canAutoprint, markEvent]);

  const enabled = isReady;
  const label = isReady ? "Print" : "Preparing…";

  // Snapshot pending count when the tooltip opens so the text doesn't
  // mutate while the user reads it.
  const [snap, setSnap] = useState<{ pending: number }>({ pending: 0 });

  const tooltip = !isReady
    ? snap.pending > 0
      ? `Preparing ${snap.pending} diagram${snap.pending === 1 ? "" : "s"}. The button will enable when the page is ready — see progress at the bottom.`
      : "Preparing the report. The button will enable when the page is ready — see progress at the bottom."
    : "";

  const button = (
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handleClickPrint}
      disabled={!enabled}
      data-testid="print-launch-button"
    >
      {label}
    </Button>
  );

  // AntD 4 doesn't forward mouse events to a disabled <Button>, so the
  // tooltip won't fire while the button shows "Preparing…" without the
  // <span> wrapper.
  return tooltip ? (
    <Tooltip
      title={tooltip}
      placement="bottomRight"
      onOpenChange={(visible: boolean) => {
        if (visible) setSnap({ pending: pendingRaster });
      }}
    >
      <span style={{ display: "inline-block" }}>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

export default OptimizedPrintView;
